<script lang="ts">
  import PanelTopBarLayout from "/@/lib/components/PanelTopBarLayout.svelte"
  import { twApiClient } from "/@/lib/tastyworks-rest"
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import { notesStore } from "/@/stores/NotesStore"
  import NoteDeleteDialog from "/@lib/components/dialog/NoteDeleteDialog.svelte"
  import NoteDialog from "/@lib/components/dialog/NoteDialog.svelte"
  import AwaitItemsResponse from "/@lib/components/loading/AwaitItemsResponse.svelte"
  import ClickableTable from "/@lib/components/table/ClickableTable.svelte"
  import { TABLE_STORAGE_KEYS } from "/@lib/components/table/column-layout"
  import {
    ALWAYS_EXCLUDED_TYPES,
    NOTES_FILTER_OPTIONS,
    NOTES_OPTIONS,
  } from "/@lib/constants/notesOptions"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import { formatDateTime } from "/@lib/utils/formats"
  import {
    AdminNotes,
    type CustomerAccountRowResult,
    type ItemsResponse,
  } from "@tastyworks/tastyworks-api"
  import { Button, Checkbox, Input, Select } from "@tastyworks/ui-library"
  import DOMPurify from "dompurify"

  const AUTOMATED_NOTE_CREATOR =
    "customer-communications-api@tastyworks.systems"

  // Automated notes ship HTML markup we want to render formatted (ATP-13295);
  // agent-composed notes are plain text where whitespace and line breaks must
  // survive (ATP-13315). The body is rendered into an iframe via `srcdoc`,
  // so we either sanitize the HTML or escape + wrap in a pre-wrap container.
  const escapeHtml = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")

  const noteBodySrcdoc = (note: AdminNotes | null): string => {
    if (!note) return ""
    const body = note.body ?? ""
    if (note.createdBy === AUTOMATED_NOTE_CREATOR) {
      return DOMPurify.sanitize(body)
    }
    return `<div style="white-space: pre-wrap; font-family: sans-serif; font-size: 14px;">${escapeHtml(body)}</div>`
  }

  const NOTES_COLUMNS: ColumnDefinitions = [
    {
      name: "Subject",
      dataFormatter: (note: AdminNotes) => note.subject,
    },
    { name: "Type", dataFormatter: (note: AdminNotes) => note.noteType },
    { name: "Subtype", dataFormatter: (note: AdminNotes) => note.subType },
    {
      name: "Updated By",
      dataFormatter: (note: AdminNotes) => note.updatedBy,
    },
    {
      name: "Updated At",
      dataFormatter: (note: AdminNotes) => formatDateTime(note.updatedAt),
      comparator: (note: AdminNotes) => note.updatedAt,
    },
    {
      name: "Created By",
      dataFormatter: (note: AdminNotes) => note.createdBy,
    },
    {
      name: "Created At",
      dataFormatter: (note: AdminNotes) => formatDateTime(note.createdAt),
      comparator: (note: AdminNotes) => note.createdAt,
    },
  ]

  const isChecked = $state(Array(NOTES_FILTER_OPTIONS.length).fill(false))
  const editedNoteSubtypeOptions = $derived(
    NOTES_OPTIONS.filter(
      (option) => option.header === editedNoteCache?.noteType
    )?.[0]?.key ?? []
  )
  let notesPromise: Promise<ItemsResponse<AdminNotes> | null> = $state(
    Promise.resolve(null)
  )
  let selectedNote: AdminNotes | null = $state(null)
  let removeAutomated = $state(false)
  let isEditMode = $state(false)
  let editedNoteCache: AdminNotes | null = $state(null)
  let previousSelectedNoteId: number | null = $state(null)
  let isInitialEditMount: boolean = $state(false)

  const removeAutomatedNotes = (notes: AdminNotes[]) => {
    return notes.filter((note) => note.createdBy !== AUTOMATED_NOTE_CREATOR)
  }

  function searchNotes(
    selectedCustomerAccount: CustomerAccountRowResult,
    excludedTypes: string[]
  ): Promise<ItemsResponse<AdminNotes> | null> {
    selectedNote = null

    if (!selectedCustomerAccount.account) {
      return Promise.reject(new Error("No account selected"))
    }
    return twApiClient.AdminNotesService.index(
      selectedCustomerAccount.account?.accountNumber ?? "",
      excludedTypes
    )
  }

  function refreshNotes() {
    notesPromise = searchNotes(
      $selectedCustomerAccount,
      $notesStore.excludedTypes
    )
  }

  // ATP-14088: dispatches the same note to each account number selected
  // in NoteDialog's multi-select. The backend API is single-account-per
  // -call (POST /admin/accounts/{n}/notes), so we fan out via
  // Promise.allSettled and log per-account failures rather than aborting
  // the whole batch on a single rejection. Notes are non-destructive
  // (the user can delete later if a partial-failure ends up posting
  // something they didn't intend), so this fail-soft posture is the
  // right call.
  async function createNewNote(values: {
    subject: string
    type: string
    subType: string
    body: string
    accountNumbers: string[]
  }) {
    const results = await Promise.allSettled(
      values.accountNumbers.map((accountNumber) =>
        twApiClient.AdminNotesService.createNewNote(
          accountNumber,
          values.subject,
          values.type,
          values.subType,
          values.body
        )
      )
    )
    results.forEach((result, i) => {
      if (result.status === "rejected") {
        console.error(
          "Failed to create note on account",
          values.accountNumbers[i],
          result.reason
        )
      }
    })
    // Refresh only the currently-viewed account's notes list. If the
    // user dispatched to other accounts as well, they can switch into
    // those rows to verify; refreshing N notes lists for accounts not
    // currently in view would waste N requests for no UI benefit.
    refreshNotes()
  }

  async function deleteNote() {
    if (!selectedNote) return
    await twApiClient.AdminNotesService.deleteNote(
      $selectedCustomerAccount.account?.accountNumber ?? "",
      selectedNote
    )
    refreshNotes()
  }

  async function saveNote() {
    if (isEditMode && selectedNote) {
      if (!editedNoteCache) return
      await twApiClient.AdminNotesService.updateNote(
        $selectedCustomerAccount.account?.accountNumber ?? "",
        editedNoteCache
      )
      refreshNotes()
    }
    isEditMode = false
  }

  function exitEditMode() {
    isEditMode = false
    editedNoteCache = null
  }

  function enterEditMode() {
    isEditMode = true
    isInitialEditMount = true
    editedNoteCache = Object.assign(new AdminNotes(), selectedNote ?? null)
  }

  function closeNote() {
    exitEditMode()
    selectedNote = null
  }

  function determineExcludedTypes(concatOptions: string): string[] {
    let excludedTypes =
      concatOptions === ""
        ? []
        : NOTES_FILTER_OPTIONS.filter((_, i) => !isChecked[i])
    return excludedTypes.concat(ALWAYS_EXCLUDED_TYPES)
  }

  function handleNoteTypeChange(option: string) {
    if (isInitialEditMount) {
      isInitialEditMount = false
      return
    }
    if (editedNoteCache) {
      editedNoteCache = {
        ...editedNoteCache,
        noteType: option,
        subType: "",
      }
    }
  }

  function handleNoteSubtypeChange(option: string) {
    if (isInitialEditMount) return
    if (editedNoteCache) {
      editedNoteCache.subType = option
    }
  }

  let checkedFilterOptions = $derived(
    isChecked
      .map((checked, i) => (checked ? NOTES_FILTER_OPTIONS[i] : null))
      .filter(Boolean)
      .join(", ")
  )

  $effect(() => {
    $notesStore.excludedTypes = determineExcludedTypes(checkedFilterOptions)
  })
  $effect(() => {
    notesPromise = searchNotes(
      $selectedCustomerAccount,
      $notesStore.excludedTypes
    )
  })
  $effect(() => {
    if (selectedNote && selectedNote.id !== previousSelectedNoteId) {
      isEditMode = false
      editedNoteCache = null
      previousSelectedNoteId = selectedNote.id
    }
  })
</script>

<div class="flex grow flex-col">
  <PanelTopBarLayout>
    <div class="flex flex-row gap-3">
      <Button
        variant="secondary"
        onclick={() => {
          refreshNotes()
        }}>Refresh</Button
      >
      <!-- A `panel` snippet opts out of Select.Root's default SelectPortal, so
           portal it back out: rendered inline the panel is painted through by
           the notes table below it. Its z-index lives on the popper wrapper in
           app.css, not here. -->
      <Select.Root placeholder="" class="w-fit min-w-32 align-top">
        {#snippet panel()}
          <Select.Portal>
            <Select.Panel>
              {#each NOTES_FILTER_OPTIONS as option, i}
                <Select.Option value={option} class="p-0">
                  <!-- oxlint-disable-next-line svelte/no-useless-children-snippet -->
                  {#snippet children()}
                    <button
                      onclick={(e) => {
                        e.stopPropagation()
                        isChecked[i] = !isChecked[i]
                      }}
                      class="flex w-full items-center justify-start px-1 py-1"
                    >
                      <!-- Presentational: the wrapping button owns the toggle,
                           so the checkbox must not answer clicks of its own. -->
                      <Checkbox
                        checked={isChecked[i]}
                        class="pointer-events-none"
                        tabindex={-1}
                        aria-hidden="true"
                      />
                      <span class="mx-1">{option}</span>
                    </button>
                  {/snippet}
                </Select.Option>
              {/each}
            </Select.Panel>
          </Select.Portal>
        {/snippet}
      </Select.Root>
    </div>
    <p class="max-w-[40%] text-xs">{checkedFilterOptions}</p>

    <div class="flex max-w-[50%] gap-2">
      <div class="items-bottom flex items-center justify-end">
        <Checkbox bind:checked={removeAutomated} label="Remove automated" />
      </div>
      <NoteDialog onCreateNewNote={createNewNote} />
    </div>
  </PanelTopBarLayout>

  <div class="border-trade-desk-gray flex h-0 grow justify-center border-2">
    <AwaitItemsResponse
      promise={notesPromise}
      defaultMessage="Search for a note"
      noResultsMessage="No Notes Found"
    >
      {#snippet children({ items }: { items: AdminNotes[] })}
        <ClickableTable
          storageKey={TABLE_STORAGE_KEYS.notes}
          columns={NOTES_COLUMNS}
          items={removeAutomated ? removeAutomatedNotes(items) : items}
          onItemSelected={(item) => {
            selectedNote = item as AdminNotes
          }}
        />
      {/snippet}
    </AwaitItemsResponse>
  </div>
  {#key $selectedCustomerAccount}
    {#if selectedNote}
      <div
        class="border-trade-desk-gray flex basis-1/2 flex-col gap-2 overflow-y-scroll border-4 p-3"
      >
        <div class="flex flex-row gap-8">
          <div class="flex w-14 shrink-0 grow-0 items-center">Subject:</div>
          {#if isEditMode && editedNoteCache}
            <Input
              type="text"
              name="subject"
              class="h-6 rounded-none border-0 bg-white pl-2"
              containerClass="border-trade-desk-gray-default w-1/2 border-4 h-8"
              bind:value={editedNoteCache.subject}
              data-testid="edit-subject-input"
            />
          {:else}
            <div class="flex h-8 items-center">
              {selectedNote?.subject ?? ""}
            </div>
          {/if}
        </div>
        <div class="flex flex-row gap-8">
          <div class="flex w-14 shrink-0 grow-0 items-center">Type:</div>
          {#if isEditMode && editedNoteCache}
            <Select.Root
              value={editedNoteCache.noteType}
              class="w-1/2"
              onchange={({ value }) =>
                handleNoteTypeChange(String(value || ""))}
            >
              {#snippet trigger()}
                <Select.Trigger
                  class="border-trade-desk-gray-default h-8 border-4 bg-white pl-1"
                  data-testid="note-type-select"
                />
              {/snippet}
              <Select.Content>
                {#each NOTES_OPTIONS as option}
                  <Select.Option
                    value={option.header}
                    label={option.header}
                    class="flex h-6 flex-row items-center justify-between"
                  >
                    {option.header}
                  </Select.Option>
                {/each}
              </Select.Content>
            </Select.Root>
          {:else}
            <div class="flex h-8 items-center">
              {selectedNote?.noteType ?? ""}
            </div>
          {/if}
        </div>
        <div class="flex flex-row gap-8">
          <div class="flex w-14 shrink-0 grow-0 items-center">SubType:</div>
          {#if isEditMode && editedNoteCache}
            {#key editedNoteCache.noteType}
              <Select.Root
                class="w-1/2"
                value={editedNoteCache.subType}
                onchange={({ value }) =>
                  handleNoteSubtypeChange(String(value || ""))}
              >
                {#snippet trigger()}
                  <Select.Trigger
                    class="border-trade-desk-gray-default h-8 border-4 bg-white pl-1"
                    data-testid="note-subtype-select"
                  />
                {/snippet}
                <Select.Content>
                  <Select.Option
                    value=""
                    label=""
                    class="flex h-6 flex-row items-center justify-end"
                  />
                  {#each editedNoteSubtypeOptions as option (option)}
                    <Select.Option
                      value={option}
                      label={option}
                      class="flex h-6 flex-row items-center justify-between"
                    >
                      {option}
                    </Select.Option>
                  {/each}
                </Select.Content>
              </Select.Root>
            {/key}
          {:else}
            <div class="flex h-8 items-center">
              {selectedNote?.subType ?? ""}
            </div>
          {/if}
        </div>
        <div class="flex grow flex-row gap-8">
          <div class="flex w-14 shrink-0 grow-0 items-center">Body:</div>
          {#if isEditMode}
            <textarea
              class="border-trade-desk-gray-default h-full w-full border-4 p-2"
              value={selectedNote?.body ?? ""}
              onchange={(e) => {
                if (editedNoteCache)
                  editedNoteCache.body = e.currentTarget.value
              }}
              data-testid="note-body-textarea"></textarea>
          {:else}
            <iframe
              title="notes details"
              srcdoc={noteBodySrcdoc(selectedNote)}
              sandbox="allow-same-origin"
              class="border-trade-desk-gray-default h-full w-full border-4"
              data-testid="note-body-iframe"
            ></iframe>
          {/if}
        </div>
        <div class="mt-2 flex flex-row items-end justify-between">
          <div class="flex basis-1/6">Actions:</div>
          <div class="flex justify-between gap-3">
            <NoteDeleteDialog on:deleteNote={deleteNote} />
            {#if isEditMode}
              <Button
                class="w-20"
                variant="secondary"
                onclick={saveNote}
                data-testid="note-save-button"
                >Save
              </Button>
              <Button
                class="w-20"
                variant="secondary"
                onclick={exitEditMode}
                data-testid="note-cancel-edit-button">Cancel</Button
              >
            {:else}
              <Button class="w-20" variant="secondary" onclick={enterEditMode}
                >Edit</Button
              >
            {/if}
            <Button
              variant="secondary"
              onclick={closeNote}
              data-testid="note-close-button">Close</Button
            >
          </div>
        </div>
      </div>
    {/if}
  {/key}
</div>
